import { Box, Card, CardContent, styled } from '@mui/material';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DateFormats, useFormatting } from '@/utils/formatting';
import { CompanyPaymentDetailsResponse } from '@/models/payment';
import { UserContext } from '@/contexts/user/UserContext';
import dayjs from 'dayjs';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    PageSubHeading,
    TextDisplaySMMedium,
    TextLGRegular,
    TextLGRegularBrand,
    TextLGSemiBoldBrand
} from '@/typography/Typography';

const StyledCard = styled(Card)`
    display: flex;
    height: 222px;
    margin-top: 24px;
    margin-bottom: 24px;
    padding: 24px 16px;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    align-self: stretch;
    border-radius: 8px;
    background: var(--Brand-600, #1570ef);
    /* Shadow/sm */
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.4);
`;
const StyledCardContent = styled(CardContent)`
    display: flex;
    padding: 16px;
    align-items: center;
    gap: 32px;
    justify-content: space-between;
    align-self: stretch;
`;

const VerticalDivider = styled('div')`
    width: 1px;
    background: #fff;
    height: 80px;
`;

export const SubscriptionDetails = () => {
    const [company, setCompany] = useState<CompanyPaymentDetailsResponse>();
    const { setLoading } = useLoader();
    const { backendErrorHandler } = useErrorHandling();
    const { formatDate, getBalanceLabel } = useFormatting();

    const fetchCompany = useCallback(() => {
        setLoading(true);
        PaymentsAPIServiceV2.getInstance()
            .getCurrentCompany()
            .then((response: any) => {
                var company = response.data as CompanyPaymentDetailsResponse;
                setCompany(company);
            })
            .catch(backendErrorHandler())
            .finally(() => {
                setLoading(false);
            });
    }, [setLoading, backendErrorHandler]);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    const { company: userCompany } = useContext(UserContext);

    const getNextPeriodStartIfNoTrial = () => {
        return dayjs()
            .add(1, 'month')
            .startOf('month')
            .format(DateFormats.DATE_WITH_SHORT_MONTH);
    };

    const getNextPeriodStart = () => {
        if (userCompany?.endOfTrial) {
            let nextPeriodStart = dayjs(userCompany?.endOfTrial)
                .add(1, 'month')
                .startOf('month');

            if (nextPeriodStart.isAfter(dayjs())) {
                var nextPeriodStartStr = nextPeriodStart.format(
                    DateFormats.DATE_WITH_SHORT_MONTH
                );
                return nextPeriodStartStr;
            }
        }
        return getNextPeriodStartIfNoTrial();
    };

    return (
        <>
            {userCompany?.endOfTrial && (
                <PageSubHeading>
                    Free trial ending :{' '}
                    {formatDate(
                        userCompany?.endOfTrial,
                        DateFormats.DATE_WITH_SHORT_MONTH
                    )}
                </PageSubHeading>
            )}
            <Box>
                <StyledCard>
                    <TextLGSemiBoldBrand alignSelf={'start'}>
                        Plan
                    </TextLGSemiBoldBrand>
                    <StyledCardContent>
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                InstaPaid Basic
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                Monthly subscription
                            </TextLGRegularBrand>
                        </div>
                        <VerticalDivider />
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                {getBalanceLabel(
                                    company?.companyServiceFee,
                                    company?.currency ?? 'DKK'
                                )}
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                {' '}
                                Monthly service fee
                            </TextLGRegularBrand>
                        </div>
                        <VerticalDivider />
                        <div style={{ marginRight: 'auto' }}>
                            <TextDisplaySMMedium>
                                {getBalanceLabel(
                                    company?.pricePerUser,
                                    company?.currency ?? 'DKK'
                                )}{' '}
                                per user
                            </TextDisplaySMMedium>
                            <TextLGRegularBrand>
                                {' '}
                                {company?.activeUsersCount} Activated users
                            </TextLGRegularBrand>
                        </div>
                    </StyledCardContent>
                    <div
                        style={{
                            alignSelf: 'start',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px'
                        }}
                    >
                        <TextLGSemiBoldBrand alignSelf={'start'}>
                            Next period starts:
                        </TextLGSemiBoldBrand>
                        <TextLGRegular> {getNextPeriodStart()}</TextLGRegular>
                    </div>
                </StyledCard>
            </Box>
        </>
    );
};
